import classNames from 'classnames';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { Button, Input, RadioOptions } from 'components';
import { Switch } from 'components-antd';
import { Col, Row } from 'components-antd/Grid';
import { useDispatch } from 'react-redux';
import { AGENT, TEAM_OWNER } from 'settings/constants/roles';
import {
  appSetInviteTeamMemberValuesAction,
  appSetShowAssignConnectionAction,
} from 'store/actions/app';
import { appSetConnectionTypeEffect, requestGetTeamListEffect } from 'store/effects';
import { getAgentTeamRoleSelector } from 'store/selectors/agentTeamDetail';
import { TitleInput } from '../TitleInput';
import { schema } from './validation';

import styles from './styles.module.scss';
import { AGENT_CONNECTIONS } from 'settings/constants/drawers';

const Details = ({ handleNext }) => {
  const [options, setOptions] = useState([{ id: 'Basic', label: 'Member', value: 'Basic' }]);
  const [isLoading, setIsLoading] = useState(false);
  const agentRole = useSelector(getAgentTeamRoleSelector);
  const dispatch = useDispatch();

  useEffect(() => {
    if (agentRole === TEAM_OWNER)
      setOptions([...options, { id: 'Admin', label: 'Admin', value: 'Admin' }]);
  }, []);

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      role: '',
      canCreateTransaction: false,
    },
    validateOnMount: true,
    validationSchema: schema,
    onSubmit: (values) => {
      setIsLoading(true);
      dispatch(appSetInviteTeamMemberValuesAction(values));
      dispatch(appSetConnectionTypeEffect({ type: AGENT_CONNECTIONS.Team }));
      dispatch(
        appSetShowAssignConnectionAction({
          open: false,
          assignType: AGENT,
          newInvite: true,
        }),
      );
      dispatch(requestGetTeamListEffect());
      handleNext();
    },
  });
  const {
    values,
    touched,
    errors,
    isValid,
    setFieldValue,
    handleSubmit,
    handleChange,
    handleBlur,
  } = formik;

  const handleRadioOptionChange = (value) => {
    setFieldValue('role', value);
  };

  const isDisabled = () => (!values?.titleFreeText && !values?.title?.name) || isLoading;

  return (
    <form testid="invite_buyer_form" onSubmit={handleSubmit}>
      <h1 className={styles.heading}>Invite Member</h1>
      <div className={styles.formContainer}>
        <div className={styles.card}>
          <div className={styles.content}>
            <p className={styles.label}>Contact Information</p>
            <div className={styles.contactInformation}>
              <Row className={styles.inputRow} gutter={20}>
                <Col xs={24} md={12}>
                  <Input
                    showAsterisk={true}
                    name="firstName"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.firstName}
                    label="First name"
                    error={touched.firstName ? errors.firstName : ''}
                    testid="first_name"
                    placeholder={'Enter first name'}
                    maxLength={'30'}
                    variant={Input.LIGHT_ROUND}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <Input
                    showAsterisk={true}
                    name="lastName"
                    onChange={handleChange}
                    value={values.lastName}
                    onBlur={handleBlur}
                    label="Last name"
                    error={touched.lastName ? errors.lastName : ''}
                    testid="last_name"
                    placeholder={'Enter last name'}
                    maxLength={'30'}
                    variant={Input.LIGHT_ROUND}
                  />
                </Col>
              </Row>
              <Row className={styles.inputRow}>
                <Col span={24}>
                  <Input
                    showAsterisk={true}
                    name="email"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email}
                    label="Email"
                    error={touched.email ? errors.email : ''}
                    testid="email"
                    placeholder={'Enter email address...'}
                    maxLength={'50'}
                    variant={Input.LIGHT_ROUND}
                  />
                </Col>
              </Row>
            </div>
            <Row className={classNames(styles.inputRow, styles.memberRole)}>
              <Col span={24}>
                <p className={styles.subHeading}>Role</p>
                <RadioOptions
                  options={options}
                  name="role"
                  onChange={handleRadioOptionChange}
                  onBlur={handleBlur}
                  value={values.role}
                  error={touched.role ? errors.role : ''}
                  testid="client_type"
                  className={styles.selectType}
                  optionClassName={styles.selectOption}
                  checkedClassName={styles.selectChecked}
                  labelTextClassName={styles.selectLabel}
                />
              </Col>
            </Row>
            <Row className={styles.inputRow}>
              <Col span={24}>
                <TitleInput
                  values={values}
                  setFieldValue={setFieldValue}
                  className={styles.titleInput}
                />
              </Col>
            </Row>
            <div className={styles.transaction}>
              Allow Transaction Creation
              <Switch
                className={styles.switch}
                onChange={(checked) => {
                  setFieldValue('canCreateTransaction', checked);
                }}
                value={values.canCreateTransaction}
              />
            </div>
          </div>
        </div>
        <div className={styles.footer}>
          <Button
            disabled={isLoading || isDisabled() || !isValid}
            type="submit"
            className={classNames({
              [styles.button]: true,
              [styles.active]: isValid && !isDisabled() && !isLoading,
            })}
            title="Send Invitation"
            isPending={isLoading}
          />
        </div>
      </div>
    </form>
  );
};

export default Details;
